import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Lang } from "@/components/nutrimart/types";

interface Metrics {
  pending: number;
  avgApprovalMs: number | null;
  rejectionRate: number | null; // 0..1
  windowDays: number;
}

/**
 * Pequeño widget de métricas para la pestaña de pagos del AdminPanel:
 *   - Pagos pendientes (status = 'pending')
 *   - Tiempo promedio de aprobación (updated_at − created_at de los `captured`)
 *   - Tasa de rechazo: failed / (failed + captured) en la ventana
 */
export function AdminPaymentsMetrics({
  lang,
  windowDays = 30,
}: {
  lang: Lang;
  windowDays?: number;
}) {
  const [m, setM] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const since = new Date(Date.now() - windowDays * 24 * 3600_000).toISOString();
      const [{ count: pending }, captured, failed] = await Promise.all([
        supabase
          .from("payment_transactions")
          .select("id", { count: "exact", head: true })
          .eq("status", "pending")
          .in("provider", ["manual_transfer", "cod"]),
        supabase
          .from("payment_transactions")
          .select("created_at, updated_at")
          .eq("status", "captured")
          .gte("updated_at", since),
        supabase
          .from("payment_transactions")
          .select("id", { count: "exact", head: true })
          .eq("status", "failed")
          .gte("updated_at", since),
      ]);

      if (cancelled) return;

      const capturedRows = (captured.data ?? []) as Array<{ created_at: string; updated_at: string }>;
      const capturedCount = capturedRows.length;
      const avgApprovalMs = capturedCount
        ? capturedRows.reduce(
            (s, r) => s + (new Date(r.updated_at).getTime() - new Date(r.created_at).getTime()),
            0,
          ) / capturedCount
        : null;

      const failedCount = failed.count ?? 0;
      const total = capturedCount + failedCount;
      const rejectionRate = total > 0 ? failedCount / total : null;

      setM({
        pending: pending ?? 0,
        avgApprovalMs,
        rejectionRate,
        windowDays,
      });
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [windowDays]);

  if (loading || !m) {
    return (
      <div data-testid="admin-payments-metrics" style={{ fontSize: 12, color: "var(--tx3)", padding: "8px 0" }}>
        {lang === "es" ? "Cargando métricas…" : "Loading metrics…"}
      </div>
    );
  }

  const card: React.CSSProperties = {
    background: "var(--bg2)",
    border: "1px solid var(--bd2)",
    borderRadius: "var(--r2)",
    padding: "10px 12px",
    flex: 1,
    minWidth: 120,
  };
  const lbl: React.CSSProperties = { fontSize: 10, color: "var(--tx3)", textTransform: "uppercase", fontWeight: 700 };
  const val: React.CSSProperties = { fontSize: 20, fontWeight: 700, marginTop: 2, color: "var(--tx)" };
  const sub: React.CSSProperties = { fontSize: 10, color: "var(--tx3)", marginTop: 2 };

  return (
    <div
      data-testid="admin-payments-metrics"
      style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "8px 0 14px" }}
    >
      <div style={card}>
        <div style={lbl}>{lang === "es" ? "Pagos pendientes" : "Pending payments"}</div>
        <div style={val}>{m.pending}</div>
        <div style={sub}>{lang === "es" ? "esperando decisión" : "awaiting review"}</div>
      </div>
      <div style={card}>
        <div style={lbl}>{lang === "es" ? "Tiempo prom. aprobación" : "Avg approval time"}</div>
        <div style={val}>{m.avgApprovalMs == null ? "—" : formatDuration(m.avgApprovalMs, lang)}</div>
        <div style={sub}>{lang === "es" ? `últimos ${m.windowDays} días` : `last ${m.windowDays} days`}</div>
      </div>
      <div style={card}>
        <div style={lbl}>{lang === "es" ? "Tasa de rechazo" : "Rejection rate"}</div>
        <div style={val}>{m.rejectionRate == null ? "—" : `${Math.round(m.rejectionRate * 100)}%`}</div>
        <div style={sub}>{lang === "es" ? `últimos ${m.windowDays} días` : `last ${m.windowDays} days`}</div>
      </div>
    </div>
  );
}

function formatDuration(ms: number, lang: Lang) {
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins} ${lang === "es" ? "min" : "min"}`;
  const hours = Math.round((mins / 60) * 10) / 10;
  if (hours < 48) return `${hours} ${lang === "es" ? "h" : "h"}`;
  const days = Math.round((hours / 24) * 10) / 10;
  return `${days} ${lang === "es" ? "d" : "d"}`;
}